import { webClient, type PlanletsClient } from '../api/client';
import { authConfigFor } from '../auth/config';
import type { PendingLogin } from '../auth/flow';
import { navigate } from '../auth/redirect';
import { rememberReturnTo, takeReturnTo } from '../auth/returnTo';
import type { Platform } from './platform';

const PENDING_KEY = 'planlets.pendingLogin';
const LANGUAGE_KEY = 'planlets.language';

/** The page location the web platform reads: which hostname (→ issuer) and origin (→ callback). */
export interface PageLocation {
  readonly hostname: string;
  readonly origin: string;
}

/** The sessionStorage subset the in-flight login rides across the redirect. */
export type PendingStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The localStorage subset the page's settings — the language choice (ADR-031) — live in. */
export type SettingsStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * The browser install's `Platform` (ADR-029 §2): the issuer for the page's hostname
 * (`src/auth/config.ts`), `{origin}/auth/callback` as the registered redirect URI, the
 * same-origin `/api` client, the in-flight login in sessionStorage for the redirect round-trip,
 * a full-page navigation to the authorize URL, the resume target of launch decision 12
 * (`src/auth/returnTo.ts`), no persisted session (tokens stay in memory — ADR-017 §5), no
 * host events (the page's own URL carries the callback and the deep links), no push channel
 * (ADR-030 §5 — the browser is where the realtime notices already land), and the language
 * choice in localStorage (ADR-031) — every access guarded, since a privacy mode throws: a
 * failure reads as no choice, a failed write is swallowed.
 */
export function createWebPlatform(
  location: PageLocation = window.location,
  store: PendingStore = sessionStorage,
  client: PlanletsClient = webClient,
  settings: SettingsStore = localStorage,
): Platform {
  const { issuer, clientId } = authConfigFor(location.hostname);
  return {
    kind: 'web',
    auth: { issuer, clientId, redirectUri: `${location.origin}/auth/callback` },
    client,
    savePending: async (login) => store.setItem(PENDING_KEY, JSON.stringify(login)),
    takePending: async () => {
      const raw = store.getItem(PENDING_KEY);
      store.removeItem(PENDING_KEY);
      return raw === null ? null : (JSON.parse(raw) as PendingLogin);
    },
    openAuth: async (url) => navigate(url),
    rememberReturnTo: async (target) => rememberReturnTo(target),
    takeReturnTo: async () => takeReturnTo(),
    loadSession: async () => null,
    storeSession: async () => undefined,
    loadLanguage: async () => {
      try {
        return settings.getItem(LANGUAGE_KEY);
      } catch {
        return null;
      }
    },
    storeLanguage: async (locale) => {
      try {
        settings.setItem(LANGUAGE_KEY, locale);
      } catch {
        // No persistent storage in this browsing mode: the choice lasts the page's lifetime.
      }
    },
    listen: () => () => undefined,
    push: null,
    unsupported: () => null,
    describeInstall: async () => null,
  };
}
